/**
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */
import React, { useMemo } from 'react';
import { t, useTheme } from '@superset-ui/core';
import { Form, FormItem } from 'src/components/Form';
import Button from 'src/components/Button';
import { ConditionalFormattingConfig } from './types';
import {
  JustifyEnd,
  SectionContent,
  SectionTitle,
} from './FormattingPopoverLayout';
import {
  RuleSectionFields,
  StyleSectionFields,
  SubscriptSectionFields,
} from './FormattingPopoverSections';
import {
  buildFormattingInitialValues,
  colorSchemeOptions,
  normalizeFormattingFormValues,
} from './formattingFormShared';

export const FormattingPopoverContent = ({
  config,
  onChange,
  columns = [],
  showSubscriptOptions = false,
  showProgressOptions = false,
}: {
  config?: ConditionalFormattingConfig;
  onChange: (config: ConditionalFormattingConfig) => void;
  columns: { label: string; value: string }[];
  showSubscriptOptions?: boolean;
  showProgressOptions?: boolean;
}) => {
  const theme = useTheme();
  const schemes = useMemo(() => colorSchemeOptions(theme), [theme]);
  const formInitialValues = useMemo(
    () => buildFormattingInitialValues(theme, columns, config),
    [theme, columns, config],
  );

  const handleFormFinish = (values: ConditionalFormattingConfig) => {
    onChange(normalizeFormattingFormValues(values));
  };

  return (
    <Form
      onFinish={handleFormFinish}
      initialValues={formInitialValues}
      layout="vertical"
    >
      <SectionTitle>{t('规则设置')}</SectionTitle>
      <SectionContent>
        <RuleSectionFields columns={columns} />
      </SectionContent>

      <SectionTitle>{t('视觉样式')}</SectionTitle>
      <SectionContent>
        <StyleSectionFields
          colorScheme={schemes}
          config={config}
          showDisplayOptions={showProgressOptions}
        />
      </SectionContent>

      {showSubscriptOptions && (
        <>
          <SectionTitle>{t('下标与差值')}</SectionTitle>
          <SectionContent>
            <SubscriptSectionFields />
          </SectionContent>
        </>
      )}

      <FormItem>
        <JustifyEnd>
          <Button htmlType="submit" buttonStyle="primary">
            {t('Apply')}
          </Button>
        </JustifyEnd>
      </FormItem>
    </Form>
  );
};
